import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useGetSettings } from "@/api/configuration/useConfigurationAPI";
import { useSearchPlaceQuery } from "@/api/place/usePlaceAPI";
import { MakerIcon } from "@/assets/icons";
import EmptyPlaceholder from "@/components/emptyPlaceholder";
import SearchAutocomplete from "@/components/ui/searchAutocomplete";
import { Switch } from "@/components/ui/switch";
import { MAX_POI } from "@/constants/dataset";
import { MESSAGE } from "@/constants/message";
import { COORDINATE_POTENTIAL_REGEX } from "@/constants/regex";
import { useMap } from "@/hooks/map/core/useMapContext";
import useCatchmentPlaceSection from "@/hooks/spatialAnalysis/usePlaceSection";
import { useDebounce } from "@/hooks/useDebounce";
import { RootState } from "@/store";
import { setIsShowMarkerLabel } from "@/store/slice/spatialSlice";
import { PlaceItem } from "@/types/place";

import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

import MarkerDetailList from "./markerDetailList";

const TEST_ID_PREFIX = "catchment-place-section";

type Props = {
  className?: string;
};

const CatchmentPlace = ({ className }: Props) => {
  const {
    handlePinMaker,
    handleChangeNameMarker,
    handleRemoveItem,
    handleSelectPlace
  } = useCatchmentPlaceSection();
  const dispatch = useDispatch();
  // State
  const { catchmentSettingsTemp, isShowMarkerLabel } = useSelector(
    (state: RootState) => state.spatial
  );
  const { allowPinMarker } = useSelector((state: RootState) => state.ui);
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const debouncedKeyword = useDebounce(searchKeyword, 500);
  const { currentCenter, hideAllMarkerLabels, showAllMarkerLabels } = useMap();

  const isSearchByCoordinate = COORDINATE_POTENTIAL_REGEX.test(
    searchKeyword.trim()
  );

  const {
    data: placeData,
    isLoading,
    isFetching
  } = useSearchPlaceQuery(
    {
      query: debouncedKeyword,
      limit: 5,
      proximity: `${currentCenter[0]},${currentCenter[1]}`
    },
    { enabled: !!searchKeyword }
  );

  const { data } = useGetSettings();
  const MAX_CATCHMENTS = data?.max_catchments || MAX_POI;

  const renderPlaceOption = (option: PlaceItem, _keyword: string) => {
    // Add keyword argument
    return (
      <div className="flex flex-col overflow-hidden">
        <p className="truncate text-txtHeading" title={option.name}>
          {option.name} {option.full_address}
        </p>
        <span className="text-[10px]">
          {option.coordinates.latitude}, {option.coordinates.longitude}
        </span>
      </div>
    );
  };

  const handleSearchPlace = (clearSearchKeyword: () => void) => {
    if (isSearchByCoordinate) {
      const trimmedKeyword = searchKeyword.trim();

      const [latitudeStr, longitudeStr] = trimmedKeyword.split(",");
      const lat = Number(latitudeStr);
      const lng = Number(longitudeStr);

      setErrorMsg("");
      handleSelectPlace({
        address: null,
        coordinates: {
          latitude: lat,
          longitude: lng
        },
        full_address: "",
        name: "",
        type: ""
      });
      // Clear search keyword both
      clearSearchKeyword();
      setSearchKeyword("");
    }
  };

  const handleChangeShowLabel = (checked: boolean) => {
    if (checked) {
      dispatch(setIsShowMarkerLabel({ checked: true }));
      showAllMarkerLabels();
    } else {
      dispatch(setIsShowMarkerLabel({ checked: false }));
      hideAllMarkerLabels();
    }
  };

  const hasPlaceResults = Boolean(placeData?.places?.length);

  const getNodataText = (): string => {
    if (errorMsg) return errorMsg;
    if (isLoading || isFetching || isSearchByCoordinate) return "";
    if (debouncedKeyword?.length >= 3 && !hasPlaceResults) {
      return MESSAGE.CANNOT_FIND_PLACE;
    }
    return "";
  };

  return (
    <div className={className}>
      <div className="flex mt-2 gap-2">
        <SearchAutocomplete
          className="w-full"
          error={!!errorMsg}
          options={errorMsg ? [] : (placeData?.places ?? [])}
          getOptionLabel={(option) => option.name}
          getKey={(option) =>
            `${option.coordinates.latitude}-${option.coordinates.longitude}`
          }
          renderOption={renderPlaceOption}
          onSelect={handleSelectPlace}
          placeholder="Search Place"
          isSearchApi
          setSearchKeyword={(val) => {
            setSearchKeyword(val);
            if (errorMsg) setErrorMsg("");
          }}
          noDataTxt={getNodataText()}
          disabled={
            catchmentSettingsTemp.placeCoordinates?.length === MAX_CATCHMENTS
          }
          onSearch={handleSearchPlace}
          testId={TEST_ID_PREFIX}
        />
        <Button
          variant="ghost"
          className={twMerge(
            "h-[40px] w-[40px] !min-w-[40px] border border-teal-500 hover:bg-teal-100 disabled:opacity-50",
            allowPinMarker && "bg-teal-500"
          )}
          type="button"
          disabled={
            catchmentSettingsTemp.placeCoordinates?.length === MAX_CATCHMENTS
          }
          onClick={handlePinMaker}
        >
          <MakerIcon
            className={twMerge("fill-teal-500", allowPinMarker && "fill-white")}
            width={16}
            height={20}
          />
        </Button>
      </div>
      <div className="flex flex-row items-center mt-2">
        <p className="text-[10px]">
          List of marker{" "}
          <span className="text-teal-500 font-bold">
            {catchmentSettingsTemp.placeCoordinates?.length?.toLocaleString(
              "en-US"
            )}
            /{data?.max_catchments?.toLocaleString("en-US") || 100}
          </span>
        </p>
        <div className="flex flex-row items-center ml-auto">
          <span className="text-[10px] text-txtHeading mr-2">Show label</span>
          <Switch
            checked={isShowMarkerLabel}
            onCheckedChange={handleChangeShowLabel}
          />
        </div>
      </div>

      {/* Show list of markers */}
      {catchmentSettingsTemp.placeCoordinates?.length ? (
        <MarkerDetailList
          data={catchmentSettingsTemp.placeCoordinates ?? []}
          onRemoveItem={handleRemoveItem}
          onChangeMarkerName={handleChangeNameMarker}
        />
      ) : (
        <EmptyPlaceholder
          className="my-2 bg-white px-4"
          icon={
            <MakerIcon className="fill-light-gray" width={21} height={26} />
          }
          description="Please create a marker by dropping, searching, or input coordinate manually"
        />
      )}
    </div>
  );
};

export default CatchmentPlace;
